/**
 * FocusMode Context - Full-Screen Expansion System
 * 
 * Manages workspace layout, focus states, and screen transitions when
 * features are launched from the Command Palette.
 * 
 * Features:
 * - Automatic full-screen expansion when launching from Command Palette
 * - Hides sidebars, nav bars, and background widgets
 * - Persistent exit control for returning to previous layout
 * - Context restoration on exit
 * - Power-User Mode: remembers preferences for repeated launches
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { X, Minimize2, Maximize2, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const FocusModeContext = createContext(null);

// Storage key for user preferences
const FOCUS_PREFERENCES_KEY = "hos_focus_mode_preferences";
const FOCUS_HISTORY_KEY = "hos_focus_mode_history";

export function FocusModeProvider({ children }) {
  const [isFocusMode, setIsFocusMode] = useState(false);
  const [focusContent, setFocusContent] = useState(null);
  const [focusTitle, setFocusTitle] = useState("");
  const [focusIcon, setFocusIcon] = useState(null);
  const [previousState, setPreviousState] = useState(null);
  const [launchHistory, setLaunchHistory] = useState({});
  const [userPreferences, setUserPreferences] = useState({});

  // Load user preferences from localStorage
  useEffect(() => {
    try {
      const savedPrefs = localStorage.getItem(FOCUS_PREFERENCES_KEY);
      if (savedPrefs) {
        setUserPreferences(JSON.parse(savedPrefs));
      }
      const savedHistory = localStorage.getItem(FOCUS_HISTORY_KEY);
      if (savedHistory) {
        setLaunchHistory(JSON.parse(savedHistory));
      }
    } catch (e) {
      console.error("Failed to load focus mode preferences:", e);
    }
  }, []);

  // Save preferences to localStorage
  const savePreferences = useCallback((prefs) => {
    setUserPreferences(prefs);
    try {
      localStorage.setItem(FOCUS_PREFERENCES_KEY, JSON.stringify(prefs));
    } catch (e) {
      console.error("Failed to save focus mode preferences:", e);
    }
  }, []);

  // Track launch history for power-user mode
  const trackLaunch = useCallback((featureId) => {
    setLaunchHistory(prev => {
      const updated = {
        ...prev,
        [featureId]: (prev[featureId] || 0) + 1
      };
      try {
        localStorage.setItem(FOCUS_HISTORY_KEY, JSON.stringify(updated));
      } catch (e) {
        console.error("Failed to save launch history:", e);
      }
      return updated;
    });
  }, []);

  // Check if feature should auto-expand (Power-User Mode)
  const shouldAutoExpand = useCallback((featureId) => {
    // Auto-expand if launched 3+ times OR user has set preference
    const launchCount = launchHistory[featureId] || 0;
    const userPref = userPreferences[featureId];
    
    if (userPref === "always_fullscreen") return true;
    if (userPref === "never_fullscreen") return false;
    return launchCount >= 3; // Power-user threshold
  }, [launchHistory, userPreferences]);

  // Enter focus mode
  const enterFocusMode = useCallback(({ 
    content, 
    title, 
    icon,
    featureId,
    forceFullscreen = false
  }) => {
    // Track this launch
    if (featureId) {
      trackLaunch(featureId);
    }

    // Save current state for restoration
    setPreviousState({
      scrollY: window.scrollY,
      overflow: document.body.style.overflow,
      timestamp: Date.now()
    });

    // Prevent body scroll
    document.body.style.overflow = "hidden";

    // Set focus content
    setFocusContent(content);
    setFocusTitle(title);
    setFocusIcon(icon);
    setIsFocusMode(true);

    // Dispatch event for other components to react
    window.dispatchEvent(new CustomEvent("focusmodeenter", { 
      detail: { featureId, title } 
    }));
  }, [trackLaunch]);

  // Exit focus mode
  const exitFocusMode = useCallback(() => {
    // Restore previous state
    if (previousState) {
      document.body.style.overflow = previousState.overflow || "";
      window.scrollTo(0, previousState.scrollY || 0);
    }

    setIsFocusMode(false);
    setFocusContent(null);
    setFocusTitle("");
    setFocusIcon(null);

    // Dispatch event
    window.dispatchEvent(new CustomEvent("focusmodeexit"));
  }, [previousState]);

  // Toggle focus mode
  const toggleFocusMode = useCallback(() => {
    if (isFocusMode) {
      exitFocusMode();
    }
  }, [isFocusMode, exitFocusMode]);

  // Set user preference for a feature
  const setFeaturePreference = useCallback((featureId, preference) => {
    savePreferences({
      ...userPreferences,
      [featureId]: preference
    });
  }, [userPreferences, savePreferences]);

  // Keyboard handler for ESC to exit
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isFocusMode) {
        exitFocusMode();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isFocusMode, exitFocusMode]);

  return (
    <FocusModeContext.Provider value={{
      isFocusMode,
      focusContent,
      focusTitle,
      focusIcon,
      enterFocusMode,
      exitFocusMode,
      toggleFocusMode,
      shouldAutoExpand,
      setFeaturePreference,
      launchHistory,
      userPreferences
    }}>
      {children}
      
      {/* Focus Mode Overlay */}
      {isFocusMode && (
        <FocusModeOverlay
          content={focusContent}
          title={focusTitle}
          icon={focusIcon}
          onExit={exitFocusMode}
        />
      )}
    </FocusModeContext.Provider>
  );
}

// Focus Mode Overlay Component
function FocusModeOverlay({ content, title, icon, onExit }) {
  const [minimized, setMinimized] = useState(false);

  return (
    <div 
      className={`fixed inset-0 z-[100] bg-slate-950 transition-all duration-200 ${
        minimized ? "opacity-0 pointer-events-none" : "opacity-100"
      }`}
      style={{ 
        animation: "focusModeEnter 200ms ease-out"
      }}
    >
      {/* Focus Mode Header */}
      <div className="absolute top-0 left-0 right-0 h-14 bg-slate-900/95 border-b border-slate-800 backdrop-blur-sm flex items-center justify-between px-4 z-10">
        <div className="flex items-center gap-3">
          {icon && <span className="text-xl">{icon}</span>}
          <h2 className="text-lg font-semibold text-white">{title}</h2>
          <Badge className="bg-teal-500/20 text-teal-400 border-0">
            Focus Mode
          </Badge>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="text-slate-400 hover:text-white hover:bg-slate-800"
            onClick={() => setMinimized(true)}
            title="Minimize (ESC to restore)"
          >
            <Minimize2 className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="text-slate-400 hover:text-red-400 hover:bg-red-500/10"
            onClick={onExit}
            title="Exit Focus Mode"
            data-testid="exit-focus-mode"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Focus Content Area */}
      <div className="absolute top-14 left-0 right-0 bottom-0 overflow-auto bg-slate-950">
        {content}
      </div>

      {/* Minimized Indicator */}
      {minimized && (
        <div 
          className="fixed bottom-4 right-4 z-[101] animate-in slide-in-from-bottom"
          onClick={() => setMinimized(false)}
        >
          <Button
            className="bg-teal-600 hover:bg-teal-700 text-white shadow-lg"
            size="sm"
          >
            <Maximize2 className="w-4 h-4 mr-2" />
            Restore {title}
          </Button>
        </div>
      )}

      <style jsx global>{`
        @keyframes focusModeEnter {
          from {
            opacity: 0;
            transform: scale(0.98);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  );
}

// Hook to use focus mode
export function useFocusMode() {
  const context = useContext(FocusModeContext);
  if (!context) {
    throw new Error("useFocusMode must be used within a FocusModeProvider");
  }
  return context;
}

export default FocusModeContext;
